import { useState, useMemo } from "react"
import { NavLink } from "react-router-dom"
import { useLocation } from "react-router-dom"
import Icon from "@material-tailwind/react/Icon"
import { useAsyncDebounce } from "react-table"
import { Button } from "@mantine/core"
import { useNotifications } from "@mantine/notifications"
import { getAll } from "services/api.service"
import useFetchAndLoad from "hooks/useFetchAndLoad.ts"

const GlobalFilter = ({ preGlobalFilteredRows, globalFilter, setGlobalFilter }) => {
  const count = preGlobalFilteredRows.length
  const [value, setValue] = useState(globalFilter)
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined)
  }, 200)

  return (
    <span className='shrink-0 uppercase text-gray-900 font-bold tracking-wider m-4 text-sm'>
      Buscar:{" "}
      <input
        className='appearance-none border rounded px-2 py-1 text-gray-600 leading-tight focus:outline-none focus:shadow-outline'
        value={value || ""}
        onChange={(e) => {
          setValue(e.target.value)
          onChange(e.target.value)
        }}
        placeholder={`${count} registros...`}
      />
    </span>
  )
}

export function SelectColumnFilter({ column: { filterValue, setFilter, preFilteredRows, id } }) {
  // Calculate the options for filtering
  // using the preFilteredRows
  const options = useMemo(() => {
    const options = new Set()
    preFilteredRows.forEach((row) => {
      options.add(row.values[id])
    })
    return [...options.values()]
  }, [id, preFilteredRows])

  // Render a multi-select box
  return (
    <select
      className='border rounded px-1 py-1 text-gray-600 leading-tight focus:outline-none focus:shadow-outline'
      name={id}
      id={id}
      value={filterValue}
      onChange={(e) => {
        setFilter(e.target.value || undefined)
      }}>
      <option value=''>All</option>
      {options.map((option, i) => (
        <option key={i} value={option}>
          {option}
        </option>
      ))}
    </select>
  )
}

const TableHeader = ({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
  iconName,
  headerGroups,
  header,
  editable,
  setOpened,
  setIsCreation,
  nested,
  isAccessesTab,
  refreshData,
}) => {
  const location = useLocation().pathname
  const { loading, callEndpoint } = useFetchAndLoad()
  const notifications = useNotifications()

  const setHeader = (header) => {
    if (header) {
      return header
    }
    return location === "/" ? "DASHBOARD" : location.toUpperCase().replace("/", "")
  }

  return (
    <>
      <div className='flex justify-between items-center shadow-lg bg-white m-5 flex-wrap overflow-auto'>
        <div className='flex items-center'>
          {nested && (
            <NavLink
              to={nested}
              exact='true'
              className='flex items-center gap-4 text-lg text-gray-900 font-bold px-4 py-3 rounded-lg mx-1'
              activeclassname='bg-gradient-to-tr from-light-blue-500 to-light-blue-700 shadow-md'
              onClick={() => localStorage.setItem("path", nested)}>
              <Icon name={"arrow_back"} size='2xl' />
            </NavLink>
          )}
          <div className='ml-4'>
            <Icon name={iconName} size='2xl' />
          </div>
          <h4 className='uppercase text-gray-900 font-bold tracking-wider m-4 text-lg'>
            {setHeader(header)}
          </h4>
        </div>
        <div className='flex items-center flex-basis text-gray-900 font-bold tracking-wider text-sm flex-wrap'>
          {headerGroups.map((headerGroup) =>
            headerGroup.headers.map((column) =>
              column.Filter ? (
                <div className='shrink-0 m-4 ml-4' key={column.id}>
                  <label htmlFor={column.id}>{column.render("Header")}: </label>
                  {column.render("Filter")}
                </div>
              ) : null
            )
          )}

          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
          {editable && (
            <div className='flex justify-start items-center mx-4 my-4'>
              <Button
                className='bg-gray-700 text-white font-bold py-2 px-8 rounded-sm hover:bg-gray-600'
                onClick={() => {
                  setOpened(true)
                  setIsCreation(true)
                }}>
                <Icon name={"add"} size='2xl' />
                Agregar
              </Button>
            </div>
          )}

          {isAccessesTab && (
            <div className='flex justify-start items-center mx-4 my-4'>
              <Button
                className='bg-gray-700 text-white font-bold py-2 px-8 rounded-sm hover:bg-gray-600'
                onClick={async () => {
                  await callEndpoint(getAll("/access/force/close"))
                    .then((response) => {
                      if (response.status === 200 || response.status === 201) {
                        notifications.showNotification({
                          color: "green",
                          title: "Accesos cerrados",
                          message: "Todos los accesos han sido cerrados por el administrador",
                          icon: <Icon name={"check"} />,
                        })
                        refreshData()
                      }
                    })
                    .catch((error) => {
                      console.log(error)
                      notifications.showNotification({
                        color: "red",
                        title: "Error",
                        message: "Error al cerrar los accesos",
                      })
                    })
                }}>
                <Icon name={"close"} size='xl' />
                Cerrar todos los accesos
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default TableHeader
